import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    formatNumberForDimensions,
    getDimensionUnit,
    getGaugeMap,
    Literal,
    SearchFormData,
    TranslateFunc,
} from '@steelbuy/ts-shared';
import { SearchSummaryHeader } from '@steelbuy/ui-primitive';
import { getGaugeOptions } from '@steelbuy/util';

import './SearchListingsSearchSummary.scss';

export type SearchListingsSearchSummaryProps = {
    searchFormData: SearchFormData;
    children: ReactNode;
};

const MATERIAL_PROPERTIES: (keyof SearchFormData)[] = ['definition', 'materialType', 'grade', 'product'];

export const SearchListingsSearchSummary = ({ children, searchFormData }: SearchListingsSearchSummaryProps) => {
    const { t } = useTranslation('domainModel');

    const dimensionUnitKey = getDimensionUnit();
    const dimensionUnitLabel = t(`material.dimensions.units.${dimensionUnitKey}.label`);

    const materialProperties = useMemo(
        () =>
            MATERIAL_PROPERTIES.reduce<Partial<Record<keyof SearchFormData, string>>>((acc, property) => {
                const value = searchFormData[property];
                if (value) {
                    acc[property] = t(`material.${property}.value.${value}` as Literal);
                }
                return acc;
            }, {} as const),
        [searchFormData]
    );

    const gauges = useMemo(() => {
        const gaugeMap = getGaugeMap(searchFormData) ?? [];

        return getGaugeOptions(gaugeMap, t as TranslateFunc);
    }, [searchFormData]);

    const gaugeLabel = (val?: string) => gauges.find((opt) => opt.value === val)?.label ?? null;

    const formattedDimensions = useMemo(
        () =>
            [searchFormData.thickness, searchFormData.width, searchFormData.length]
                .filter(Boolean)
                .map((val) => `${formatNumberForDimensions(val)}${dimensionUnitLabel}`),
        [searchFormData]
    );

    const dimensionText = useMemo(() => {
        const properties: string[] = [];

        if (gauges.length && searchFormData.gauge) {
            const label = gaugeLabel(searchFormData.gauge.toString());
            const thicknessLabel = searchFormData.thicknessType
                ? `${label} ${t(`material.thicknessType.value.${searchFormData.thicknessType}`)}`
                : label;
            if (thicknessLabel) properties.push(thicknessLabel);
        }
        if (searchFormData.minWidth && searchFormData.maxWidth) {
            properties.push(
                `${formatNumberForDimensions(
                    searchFormData.minWidth
                )}${dimensionUnitLabel} - ${formatNumberForDimensions(searchFormData.maxWidth)}${dimensionUnitLabel}`
            );
        }

        properties.push(...formattedDimensions);

        return properties.join(' x ');
    }, [gauges, searchFormData.gauge, formattedDimensions]);

    const firstLine = materialProperties.definition ?? materialProperties.materialType ?? '';

    const secondLine = [dimensionText, materialProperties.grade, materialProperties.product].filter(Boolean).join(' ');

    return (
        <div className="search-listings-search-summary">
            <div className="search-summary">
                <div className="search-summary__header-properties-wrapper">
                    <SearchSummaryHeader
                        symbol={searchFormData.product?.toLowerCase()}
                        firstLine={firstLine}
                        secondLine={secondLine}
                        thirdLine=""
                    />
                </div>
                <div className="search-summary__children-wrapper">{children}</div>
            </div>
        </div>
    );
};
